import axios from "axios";
import {
  Change,
  ChangeDto,
  CreateChangeRequest,
  UpdateChangeRequest,
  DataEntryInfo,
  manResponseType,
} from "../interfaces";
import { toast } from "react-toastify";

export const getOnRevisionChanges = async (): Promise<ChangeDto[]> => {
  try {
    const response = await axios.get<ChangeDto[]>(
      `/api/changes/onrevision`
    );
    return response.data;
  } catch (error) {
    console.error("Error obteniendo los cambios en revisión:", error);
    throw error;
  }
};

export const getProcessedChanges = async (): Promise<Change[]> => {
  try {
    const response = await axios.get<Change[]>(
      `/api/changes/processedrevision`
    );
    return response.data;
  } catch (error) {
    console.error("Error obteniendo los cambios en revisión:", error);
    throw error;
  }
};

export const getProcessedChangesByUser = async (
  user: string
): Promise<Change[]> => {
  try {
    const response = await axios.get<Change[]>(
      `/api/changes/processedrevision`,
      {
        params: { user },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error obteniendo los cambios en revisión:", error);
    throw error;
  }
};

export const postRevision = async (
  change: CreateChangeRequest
): Promise<void> => {
  try {
    await axios.post(`/api/changes`, change);
    toast.success("Change added successfully");
  } catch (error: any) {
    toast.error(
      `Failed to submit change: ${
        error.response?.data?.message || "Unexpected error"
      }`
    );
    throw error;
  }
};

export const findDataEntryInfo = async (
  phaseId: number,
  identification: string,
  manifestationNumber: number
): Promise<DataEntryInfo> => {
  try {
    const response = await axios.get<DataEntryInfo>(
      `/api/dataentry/find-id`,
      {
        params: {
          phaseId,
          identification,
          manifestationNumber,
        },
      }
    );
    
    if (!response.data || !response.data.id) {
      throw new Error("Invalid response from server: missing required data");
    }
    
    return response.data;
  } catch (error: any) {
    console.error("Error finding DataEntry:", error);
    
    const errorMessage = error.response?.status === 404
      ? `No data found for Patient ${identification} in Phase ${phaseId} with Manifestation ${manifestationNumber}`
      : `Failed to find data entry: ${error.response?.data?.message || error.message}`;
    
    toast.error(errorMessage);
    throw error;
  }
};

export const associateValueToRule = async (
  value: string,
  manifestationNumber: string,
  applicablePhaseIds: number[],
  idManResponseAvailable: number[],
  manResponses: manResponseType[]
): Promise<[string, number]> => {
  try {
    let phaseId = 9; 
    if (applicablePhaseIds.length !== 0) {
      phaseId = applicablePhaseIds[0];
    }
    
    const dataEntryInfo = await findDataEntryInfo(
      phaseId,
      "C01P09",
      Number(manifestationNumber)
    );
    
    const id_man_response = dataEntryInfo.id_man_response;
    
    if (!manifestationNumber.endsWith("1")) {
      if (idManResponseAvailable.includes(id_man_response)) {
        const manResponsesRelated = manResponses.filter(
          (response) => response.Id === id_man_response
        );
        
        if (manResponsesRelated.length > 0) {
          const valueNumber = parseFloat(value);
          for (const response of manResponsesRelated) {
            if (response.id === valueNumber) {
              return [response.description_es, id_man_response];
            }
          }
        }
      }
    }
    
    return [value, id_man_response];
  } catch (error) {
    console.error("Error asociando el valor a la regla:", error);
    return [value, 0];
  }
};

export const approveChange = async (
  id: string,
  change: UpdateChangeRequest
): Promise<void> => {
  try {
    const response = await axios.put(
      `/api/changes/${id}/approved`,
      change
    );
    toast.success(response.data);
    console.log(response);
  } catch (error: any) {
    toast.error(`${error.response?.data || "Unexpected error"}`);
    throw error;
  }
};

export const rejectChange = async (
  id: string,
  change: UpdateChangeRequest
): Promise<void> => {
  try {
    await axios.put(`/api/changes/${id}`, change);
  } catch (error) {
    console.error("Error al rechazar un cambio:", error);
    throw error;
  }
};

export const fetchOldValue = async (dataEntryId: string): Promise<string> => {
  const emptyValue = "Empty Value";
  
  if (!dataEntryId) {
    console.error("fetchOldValue called with invalid ID:", dataEntryId);
    throw new Error("Invalid dataEntryId provided");
  }
  
  try {
    const response = await axios.get<string>(
      `/api/dataentry/${dataEntryId}/value`
    );

    const rawValue = response.data;

    let copyValue: string;
    if (rawValue === null || rawValue === "") {
      copyValue = emptyValue;
    } else if (rawValue === "0") {
      copyValue = "0";
    } else {
      copyValue = rawValue.toString();
    }

    const index = copyValue.indexOf("T");
    if (index !== -1) {
      return copyValue.substring(0, index);
    }

    return copyValue;
  } catch (error: any) {
    console.error("Error fetching Old Value:", error);
    const errorMessage = error.response?.status === 404
      ? "Data entry value not found"
      : `Failed to fetch value: ${error.response?.data?.message || error.message}`;
    
    toast.error(errorMessage);
    throw error;
  }
};